<!--@format-->
<template>
    <div class="syncro-send-company-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Send Company Info To Syncro</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="currentStep === 'company'" class="company-step">
                    <div v-if="company.errors.length > 0" class="row">
                        <div class="warnings col-md-12">
                            <red-warning-svg></red-warning-svg>
                            <p v-for="error in company.errors" :key="error" v-html="error"></p>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <app-border-section top-text="Determine how your data will be sent">
                                <app-input-radio
                                    v-model="company.selectedOption"
                                    :options="options"
                                />
                            </app-border-section>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <app-border-section
                                :top-text="`What information from ${company.name} would you like sent to Syncro?`"
                            >
                                <div class="upload-options">
                                    <div class="upload-option">
                                        <app-switch
                                            v-model="company.shouldSendInformation"
                                            size="large"
                                        >
                                            <span class="switch-label">
                                                <profile-card-svg
                                                    :gradient="{ from: '#4bc9fe', to: '#5052fe' }"
                                                />
                                                Company/About information
                                            </span>
                                        </app-switch>
                                    </div>
                                </div>
                            </app-border-section>
                        </div>
                    </div>
                    <div v-if="contacts.length > 0" class="row">
                        <div class="col-md-12">
                            <app-border-section
                                :top-text="`What information from ${company.name} would you like sent to Syncro?`"
                            >
                                <div class="upload-options">
                                    <div
                                        v-for="contact in contacts"
                                        :key="contact.id"
                                        class="upload-option"
                                    >
                                        <app-switch v-model="contact.isSelected" size="large">
                                            <span class="switch-label">
                                                <div
                                                    class="profile-image"
                                                    :style="{
                                                        'background-image': `url('${contact.profileImageUrl}')`,
                                                    }"
                                                ></div>
                                                {{ contact.name }}
                                            </span>
                                        </app-switch>
                                    </div>
                                </div>
                            </app-border-section>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <syncro-send-contact
                        :company="company"
                        :contact="selectedContacts[currentContactIndex]"
                        :index="currentContactIndex + 1"
                        :count="selectedContacts.length"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button
                        v-if="currentStep === 'contacts'"
                        class="transparent-btn"
                        :on-click="goBack"
                    >
                        Back
                    </app-button>
                    <app-button v-else class="transparent-btn" :on-click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="
                            currentStep === 'contacts' &&
                            selectedContacts.length > 1 &&
                            currentContactIndex < selectedContacts.length - 1
                        "
                        class="transparent-btn skip-btn"
                        :on-click="skipContact"
                        :disabled="isSendingContact"
                        :loading="isSendingContact"
                    >
                        Next
                    </app-button>
                    <app-button
                        v-if="currentStep === 'contacts'"
                        :on-click="processExportRequest"
                        :disabled="isSendingContact || hasSendError"
                        :loading="isSendingContact"
                    >
                        Send Contact to Syncro
                    </app-button>
                    <app-button
                        v-else
                        :on-click="processExportRequest"
                        :disabled="isSendingCompany || deactivatedSwitches"
                        :loading="isSendingCompany"
                    >
                        {{ areAnyContactsSelected ? 'Next Step' : 'Send Company to Syncro' }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppBorderSection = () => import('@/components/common/app-border-section');
const AppButton = () => import('@/components/common/app-button');
const AppInputRadio = () => import('@/components/common/app-input-radio');
const AppSwitch = () => import('@/components/common/app-switch');
const ProfileCardSvg = () => import('@/components/svg/profile-card-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SyncroSendContact = () => import('@/components/integration/syncro-send-contact');
const RedWarningSvg = () => import('@/components/svg/red-warning-svg');

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'syncro-send-company-widget',

    components: {
        'app-input-radio': AppInputRadio,
        'app-switch': AppSwitch,
        'profile-card-svg': ProfileCardSvg,
        'app-border-section': AppBorderSection,
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'syncro-send-contact': SyncroSendContact,
        'red-warning-svg': RedWarningSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            options: [
                {
                    name: 'Only send new records',
                    value: 'insert',
                },
                {
                    name: 'Send all records, updating existing records in Syncro',
                    value: 'upsert',
                },
            ],

            contacts: [],
            company: {
                id: null,
                name: null,
                clientId: null,
                syncroCompanyId: null,
                imageUrl: null,
                selectedOption: null,
                shouldSendInformation: null,
                errors: [],
            },

            currentStep: 'company',
            currentContactIndex: 0,
            isSendingCompany: false,
            isSendingContact: false,
            deactivatedSwitches: false,
            syncroCompanyLinkData: {
                syncroSite: null,
                syncroCompany: null,
            },
        };
    },

    computed: {
        areAnyContactsSelected() {
            return this.contacts.some((contact) => contact.isSelected);
        },

        selectedContacts() {
            if (this.areAnyContactsSelected) {
                return this.contacts.filter((contact) => contact.isSelected);
            } else {
                return [];
            }
        },

        syncroLink: function () {
            return `https://${this.syncroCompanyLinkData.syncroSite}.syncromsp.com/customers`;
        },

        hasSendError() {
            return this.selectedContacts[this.currentContactIndex]
                ? this.selectedContacts[this.currentContactIndex].hasSendError
                : false;
        },
    },

    watch: {
        'company.shouldSendInformation': function () {
            this.deactivatedSwitches = !(
                this.company.shouldSendInformation || this.areAnyContactsSelected
            );
        },

        areAnyContactsSelected: function () {
            this.deactivatedSwitches = !(
                this.company.shouldSendInformation || this.areAnyContactsSelected
            );
        },
    },

    created() {
        this.getData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getCompany()
                .then(this.getIntegrationInformation)
                .then(() => {
                    if (this.company.id) {
                        this.getContacts();
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(error, { error });
                });
        },

        getCompany() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Companies?$filter=Id eq ${this.widget.options.id}`)
                    .then((response) => {
                        this.createCompany(response.data.value[0]);
                        resolve();
                    })
                    .catch(() => {
                        reject();
                        this.isProcessing = false;
                    });
            });
        },

        getIntegrationInformation() {
            this.$api
                .get(`/ms/syncro-api/v1/credentials/${this.company.clientId}`)
                .then((response) => {
                    if (response.data) {
                        this.syncroCompanyLinkData.syncroSite = response.data.SubDomain;
                    }
                });
        },

        createCompany(companyResponse) {
            this.company.id = companyResponse.Id;
            this.company.name = companyResponse.Name;
            this.company.clientId = companyResponse.ClientId;
            this.company.syncroCompanyId = companyResponse.SyncroCompanyId;
            this.company.imageUrl =
                companyResponse.LogoUrl || require('@/assets/images/company-avatar.png');
            this.company.selectedOption = this.options[1];
            this.company.shouldSendInformation = false;
        },

        getContacts() {
            this.$api
                .get(`/service/Contacts?$filter=CompanyId eq ${this.company.id}`)
                .then((response) => {
                    this.createContacts(response.data.value);
                    this.isProcessing = false;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your contacts.',
                        { error },
                    );
                    this.isProcessing = false;
                });
        },

        createContacts(contactsResponse) {
            this.contacts = contactsResponse.map((contact) => {
                return {
                    id: contact.Id,
                    name: this.$helpers.getName(contact.FirstName, contact.LastName),
                    profileImageUrl:
                        contact.ProfileImageUrl || require('@/assets/images/avatar.png'),
                    shouldSendInformation: true,
                    shouldSendActivities: false,
                    shouldSendOpportunities: false,
                    hasSendError: false,
                    selectedOption: 'upsert',
                    isSelected: false,
                };
            });
        },

        goToSendContactsStep() {
            this.currentStep = 'contacts';
        },

        sendCompanyToSyncro() {
            this.isSendingCompany = true;
            this.postCompany()
                .then(this.updateCompanyWithSyncroId)
                .then(() => {
                    this.createCompanySuccessMessage(this.company.syncroCompanyId);

                    if (this.selectedContacts.length > 0) {
                        this.goToSendContactsStep();
                    } else {
                        this.closeWidget();
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(error, { error });
                });
        },

        postCompany() {
            const request = {
                isUpsert: this.company.shouldSendInformation,
            };

            return new Promise((resolve, reject) => {
                this.$api
                    .post(
                        `/ms/syncro-api/v1/integrations/export/companies/${this.company.id}/${this.company.clientId}`,
                        request
                    )
                    .then(() => {
                        EventBus.$emit('company:update');
                        resolve();
                    })
                    .catch(() => {
                        reject('An error occurred while exporting your company data.');
                    })
                    .finally(() => {
                        this.isSendingCompany = false;
                    });
            });
        },

        createCompanySuccessMessage(id) {
            const actionTaken =
                this.company.selectedOption.value == 'upsert' ? 'updated' : 'inserted';
            const message = `<strong>${this.company.name}</strong> has been ${actionTaken} in Syncro.`;
            const linkToSyncroCompany = `${this.syncroLink}/${id}`;

            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: linkToSyncroCompany,
                action: null,
            });
        },

        updateCompanyWithSyncroId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Companies?$filter=Id eq ${this.company.id} &$select=SyncroCompanyId`
                    )
                    .then((response) => {
                        this.company.syncroCompanyId =
                            this.company.syncroCompanyId || response.data.value[0].SyncroCompanyId;
                        resolve();
                    })
                    .catch((response) => {
                        reject(response);
                    });
            });
        },

        sendCurrentContactToSyncro() {
            this.isSendingContact = true;
            const currentContact = this.selectedContacts[this.currentContactIndex];

            const request = {
                isUpsert: this.company.selectedOption.value == 'upsert',
                exportContacts: currentContact.shouldSendInformation,
                exportDeals: currentContact.shouldSendOpportunities,
            };

            this.$api
                .post(
                    `/ms/syncro-api/v1/integrations/export/contacts/${currentContact.id}/${this.company.clientId}`,
                    request
                )
                .then(() => {
                    this.processSentContact(currentContact.id);

                    if (this.currentContactIndex === this.selectedContacts.length) {
                        this.closeWidget();
                    }
                })
                .catch(() => {
                    this.isSendingContact = false;
                    this.selectedContacts[this.currentContactIndex].hasSendError = true;
                });
        },

        processSentContact(contactId) {
            this.isSendingContact = false;

            const contact = {
                id: contactId,
                name: null,
                syncroContactId: null,
            };

            this.currentContactIndex++;

            this.hydrateSentContact(contact).then(() => {
                const contactSyncroLink = `${this.syncroLink}/${this.company.syncroCompanyId}#contacts`;

                this.createContactSuccessMessage(contactSyncroLink, contact);
            });
        },

        hydrateSentContact(contact) {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts/?$filter=Id eq ${contact.id} &$select=FirstName,LastName,SyncroContactId`
                    )
                    .then((response) => {
                        contact.name = this.$helpers.getName(
                            response.data.value[0].FirstName,
                            response.data.value[0].LastName
                        );
                        contact.syncroContactId = response.data.value[0].SyncroContactId;

                        resolve();
                    })
                    .catch(() => {
                        reject('An error occurred while fetching your Syncro integration info.');
                    });
            });
        },

        createContactSuccessMessage(link, contact) {
            var actionTaken =
                this.company.selectedOption.value == 'upsert' ? 'updated' : 'inserted';
            var message = `<strong>${contact.name}</strong> has been ${actionTaken} in Syncro.`;

            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: link,
                action: null,
            });
        },

        skipContact() {
            this.currentContactIndex++;
        },

        goBack() {
            if (this.currentContactIndex !== 0) {
                this.currentContactIndex--;
            } else {
                this.currentStep = 'company';
            }
        },

        processExportRequest() {
            if (this.currentStep == 'company') {
                this.processCompanyStep();
            } else {
                this.processContactStep();
            }
        },

        processCompanyStep() {
            if (!this.company.shouldSendInformation && this.areAnyContactsSelected) {
                this.currentStep = 'contacts';
            } else {
                this.sendCompanyToSyncro();
            }
        },

        processContactStep() {
            this.sendCurrentContactToSyncro();
        },
    },
};
</script>

<style>
.syncro-send-company-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.syncro-send-company-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.syncro-send-company-widget .action-container {
    display: flex;
}

.syncro-send-company-widget .skip-btn {
    margin-right: 15px;
}

.syncro-send-company-widget .warnings strong {
    color: #f30070;
}
</style>

<style scoped>
.syncro-send-company-widget .content-wrapper {
    padding-left: 0;
    padding-right: 0;
}

.syncro-send-company-widget .row {
    margin-bottom: 30px;
}

.syncro-send-company-widget .app-switch {
    justify-content: flex-start;
}

.syncro-send-company-widget .app-switch .switch-label {
    display: flex;
    align-items: center;
}

.syncro-send-company-widget .app-switch .switch-label svg {
    width: 28px;
    margin-right: 13px;
}

.syncro-send-company-widget .app-switch .switch-label .profile-image {
    width: 48px;
    height: 48px;
    margin-right: 12px;
    border-radius: 50%;
    background-size: cover;
    background-repeat: no-repeat;
}

.syncro-send-company-widget .warnings {
    display: flex;
    align-items: center;
    color: #f30070;
    font-size: 14px;
    max-width: 550px;
    padding-top: 5px;
}

.syncro-send-company-widget .warnings p {
    margin-left: 15px;
    margin-bottom: 0;
}
</style>
